import ExceptionAdapter, {
  type AppError,
  type NormalizeErrorOptions,
} from "@/core/exceptions/exception-adapter";

export type ErrorNormalizer = (error: unknown, options?: NormalizeErrorOptions) => AppError;

export type AsyncResult<TData> = { ok: true; data: TData } | { ok: false; error: AppError };

export interface AsyncResultDeps {
  normalizeError?: ErrorNormalizer;
}

export interface AsyncResultOptions extends NormalizeErrorOptions {
  onError?: (error: AppError) => void;
}

const defaultNormalizeError: ErrorNormalizer = (error, options) =>
  ExceptionAdapter.normalize(error, options);

export async function toResult<TData>(
  fn: () => Promise<TData>,
  options?: AsyncResultOptions,
  deps?: AsyncResultDeps,
): Promise<AsyncResult<TData>> {
  const normalizeError = deps?.normalizeError ?? defaultNormalizeError;

  try {
    return { ok: true, data: await fn() };
  } catch (caught) {
    const error = normalizeError(caught, options);
    options?.onError?.(error);
    return { ok: false, error };
  }
}

export async function resolveOrFallback<TData>(
  fn: () => Promise<TData>,
  fallback: TData,
  options?: AsyncResultOptions,
  deps?: AsyncResultDeps,
): Promise<TData> {
  const result = await toResult(fn, options, deps);
  return result.ok ? result.data : fallback;
}

export async function resolveOrNull<TData>(
  fn: () => Promise<TData>,
  options?: AsyncResultOptions,
  deps?: AsyncResultDeps,
): Promise<TData | null> {
  const result = await toResult(fn, options, deps);
  return result.ok ? result.data : null;
}

export function normalizeAppError(
  error: unknown,
  options?: NormalizeErrorOptions,
  deps?: AsyncResultDeps,
): AppError {
  return (deps?.normalizeError ?? defaultNormalizeError)(error, options);
}
